import React, { useEffect, useRef, useCallback } from "react";
import { WhatsAppMessage } from "../../types/api";
import { MessageBubble } from "./MessageBubble";

interface MessageListProps {
  messages: WhatsAppMessage[];
  currentUserId?: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onLoadMore: () => void;
}

function formatDateSeparator(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "היום";
  if (d.toDateString() === yesterday.toDateString()) return "אתמול";
  return d.toLocaleDateString("he-IL", { day: "numeric", month: "long", year: "numeric" });
}

function isSameDay(a: string, b: string) {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  currentUserId,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const prevScrollHeight = useRef(0);
  const initialScrollDone = useRef(false);

  // Sort messages by created_at ascending (oldest at top, newest at bottom)
  const sorted = [...messages].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Auto-scroll to bottom on new messages (unless user scrolled up)
  useEffect(() => {
    if (!userScrolledUp.current) {
      if (!initialScrollDone.current) {
        // Initial load: instant jump to bottom
        bottomRef.current?.scrollIntoView({ behavior: "auto" });
        initialScrollDone.current = true;
      } else {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [messages.length]);

  // Preserve scroll position when loading older messages at top
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (prevScrollHeight.current > 0) {
      container.scrollTop = container.scrollHeight - prevScrollHeight.current;
      prevScrollHeight.current = 0;
    }
  });

  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    userScrolledUp.current = distanceFromBottom > 100;
  }, []);

  // IntersectionObserver to trigger load more when sentinel is visible
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          prevScrollHeight.current = containerRef.current?.scrollHeight ?? 0;
          onLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onLoadMore]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      style={{
        flex: 1,
        overflowY: "auto",
        padding: "16px 8px",
        backgroundColor: "#e5ddd5", // WhatsApp chat background
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Top sentinel for infinite scroll */}
      <div ref={topSentinelRef} style={{ height: 1 }} />

      {isFetchingNextPage && (
        <div style={{ textAlign: "center", color: "#666", fontSize: 12, padding: "8px 0" }}>
          טוען הודעות ישנות יותר...
        </div>
      )}

      {sorted.map((msg, i) => {
        const showDateSeparator = i === 0 || !isSameDay(sorted[i - 1].created_at, msg.created_at);
        return (
          <React.Fragment key={msg.id}>
            {showDateSeparator && (
              <div style={{ display: "flex", justifyContent: "center", margin: "8px 0" }}>
                <span
                  style={{
                    backgroundColor: "rgba(255,255,255,0.8)",
                    borderRadius: 8,
                    padding: "3px 10px",
                    fontSize: 12,
                    color: "#555",
                  }}
                >
                  {formatDateSeparator(msg.created_at)}
                </span>
              </div>
            )}
            <MessageBubble
              message={msg}
              isOwn={msg.sender_user_id === currentUserId}
            />
          </React.Fragment>
        );
      })}

      {sorted.length === 0 && (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#888",
            fontSize: 14,
          }}
        >
          אין הודעות עדיין. שלח הודעה כדי להתחיל שיחה.
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};
